import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tv, MapPin, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import PublicHeader from '../../components/self-service/PublicHeader';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { useAllPublisherConfigs } from '../../data/publisherConfigs';

// us-atlas topojson — react-simple-maps' standard data source
const US_TOPOJSON = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

// FIPS state codes (us-atlas uses these as geo.id) → USPS state codes
const FIPS_TO_STATE: Record<string, string> = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE','11':'DC',
  '12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA','20':'KS','21':'KY',
  '22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN','28':'MS','29':'MO','30':'MT',
  '31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM','36':'NY','37':'NC','38':'ND','39':'OH',
  '40':'OK','41':'OR','42':'PA','44':'RI','45':'SC','46':'SD','47':'TN','48':'TX','49':'UT',
  '50':'VT','51':'VA','53':'WA','54':'WV','55':'WI','56':'WY',
};
import type { PublisherConfig } from '../../types/selfService';

// Map known DMA names to state codes (covers all currently-onboarded publishers)
const DMA_TO_STATES: Record<string, string[]> = {
  'Madison': ['WI'],
  'La Crosse-Eau Claire': ['WI', 'MN'],
  'Missoula': ['MT'],
  'Duluth-Superior': ['MN', 'WI'],
  'Knoxville': ['TN'],
  'Madison, WI': ['WI'],
  'Nashville, TN': ['TN'],
  'Honolulu, HI': ['HI'],
  'Eugene, OR': ['OR'],
  'Oklahoma City, OK': ['OK'],
  'Tulsa, OK': ['OK'],
  'Chicago': ['IL'],
  'New York': ['NY'],
  'Los Angeles': ['CA'],
};

function dmaToStates(dma: string): string[] {
  if (DMA_TO_STATES[dma]) return DMA_TO_STATES[dma];
  // Try parse "City, ST"
  const m = dma.match(/,\s*([A-Z]{2})\s*$/);
  return m ? [m[1]] : [];
}

interface PublisherEntry extends PublisherConfig {
  channelCount: number;
  marketCount: number;
  allChannels: string[];
  allDmas: string[];
  // Precomputed lowercase variants — AI suggestion scoring runs across many
  // entries × dmas × keywords and the per-iteration .toLowerCase() was the
  // hot loop. Cache these once at build time instead.
  nameLower: string;
  channelsLower: string[];
  dmasLower: string[];
  minCpm: number;
}

interface Suggestion {
  slug: string;
  score: number;
  reason: string;
}

interface AiResult {
  summary: string;
  suggestions: Suggestion[];
}

function buildEntries(configs: PublisherConfig[]): PublisherEntry[] {
  return configs.map((p) => {
    const allChannels = Array.from(new Set(p.markets.flatMap((m) => m.channels)));
    const allDmas = Array.from(new Set(p.markets.map((m) => m.dmaName)));
    const minCpm = p.products.length > 0 ? Math.min(...p.products.map((prod) => prod.cpm)) : 0;
    return {
      ...p,
      channelCount: allChannels.length,
      marketCount: allDmas.length,
      allChannels,
      allDmas,
      nameLower: p.name.toLowerCase(),
      channelsLower: allChannels.map((c) => c.toLowerCase()),
      dmasLower: allDmas.map((d) => d.toLowerCase()),
      minCpm,
    };
  });
}

const STATE_KEYWORDS: Record<string, string[]> = {
  wisconsin: ['Madison', 'La Crosse', 'Eau Claire'],
  midwest: ['Madison', 'La Crosse', 'Eau Claire', 'Duluth', 'Oklahoma', 'Tulsa', 'Chicago'],
  minnesota: ['Duluth'],
  montana: ['Missoula'],
  tennessee: ['Nashville', 'Knoxville'],
  hawaii: ['Honolulu'],
  oregon: ['Eugene'],
  oklahoma: ['Oklahoma City', 'Tulsa'],
  pacific: ['Honolulu', 'Eugene'],
  northwest: ['Eugene'],
  south: ['Nashville', 'Knoxville', 'Oklahoma City', 'Tulsa'],
  southeast: ['Nashville', 'Knoxville'],
  national: [], // matches all
};

const AUDIENCE_HINTS: Record<string, { keywords: string[]; insight: string }> = {
  news: {
    keywords: ['news', 'older', 'boomer', 'senior', 'civic', 'politics', 'local', 'engaged'],
    insight: 'Local news inventory skews older and over-indexes on engaged, civic-minded households.',
  },
  primetime: {
    keywords: ['primetime', 'family', 'household', 'evening', 'broad reach', 'awareness'],
    insight: 'Primetime broadcast delivers broad household reach in a brand-safe environment.',
  },
  sports: {
    keywords: ['sport', 'game', 'football', 'basketball', 'fan', 'male'],
    insight: 'Live broadcast affiliates carry local sports — strong for male 25-54 reach.',
  },
  vod: {
    keywords: ['young', 'millennial', 'gen z', 'streaming', 'binge', 'cord cutter', 'cheap', 'efficient'],
    insight: 'Run of Network includes VOD inventory — more efficient CPMs and skews younger.',
  },
};

function generateAiResponse(prompt: string, entries: PublisherEntry[]): AiResult {
  const text = prompt.toLowerCase();
  const matchedGeo: string[] = [];
  for (const [key, dmas] of Object.entries(STATE_KEYWORDS)) {
    if (text.includes(key)) {
      matchedGeo.push(...dmas);
      if (key === 'national') {
        entries.forEach((e) => matchedGeo.push(...e.allDmas));
      }
    }
  }
  // Direct city/market mentions
  for (const e of entries) {
    for (let i = 0; i < e.allDmas.length; i++) {
      const cityOnly = e.dmasLower[i].split(',')[0].split('-')[0].trim();
      if (text.includes(cityOnly)) matchedGeo.push(e.allDmas[i]);
    }
  }

  const insights: string[] = [];
  for (const [, info] of Object.entries(AUDIENCE_HINTS)) {
    if (info.keywords.some((k) => text.includes(k))) insights.push(info.insight);
  }

  // Lowercase matchedGeo once outside the per-entry scoring loop so the inner
  // O(entries × dmas × matchedGeo) work isn't re-lowercasing both operands.
  const matchedGeoLower = matchedGeo.map((m) => m.toLowerCase());

  const scored = entries.map((e) => {
    let score = 0;
    const reasons: string[] = [];
    const geoHits: string[] = [];
    for (let i = 0; i < e.allDmas.length; i++) {
      const dmaLower = e.dmasLower[i];
      if (matchedGeoLower.some((m) => dmaLower.includes(m))) geoHits.push(e.allDmas[i]);
    }
    if (geoHits.length > 0) {
      score += 10 * geoHits.length;
      reasons.push(`Covers ${geoHits.slice(0, 2).join(', ')}${geoHits.length > 2 ? ` +${geoHits.length - 2} more` : ''}`);
    }
    // Channel/network name match
    if (text.includes(e.nameLower) || text.includes(e.slug)) {
      score += 15;
      reasons.push(`Direct mention of ${e.name}`);
    }
    for (let i = 0; i < e.allChannels.length; i++) {
      if (text.includes(e.channelsLower[i])) {
        score += 8;
        reasons.push(`Includes ${e.allChannels[i]}`);
      }
    }
    // Budget hints
    if ((text.includes('cheap') || text.includes('efficient') || text.includes('low cpm') || text.includes('budget')) && e.minCpm <= 15) {
      score += 4;
      reasons.push(`Efficient entry CPM at $${e.minCpm}`);
    }
    if ((text.includes('premium') || text.includes('quality') || text.includes('brand')) && e.minCpm >= 20) {
      score += 3;
      reasons.push('Premium broadcast inventory');
    }
    return { entry: e, score, reason: reasons.join(' · ') || 'General match based on your description' };
  });

  scored.sort((a, b) => b.score - a.score);
  const top = scored.filter((s) => s.score > 0).slice(0, 4);
  const fallback = top.length === 0 ? scored.slice(0, 3) : top;

  let summary: string;
  if (top.length === 0) {
    summary = `I didn't find a perfect match for "${prompt.slice(0, 80)}", but here are a few publishers worth exploring. Try mentioning a specific market (e.g. "Madison, WI") or audience type for sharper recommendations.`;
  } else {
    const parts: string[] = [];
    if (matchedGeo.length > 0) parts.push(`I focused on ${Array.from(new Set(matchedGeo)).slice(0, 3).join(', ')}`);
    if (insights.length > 0) parts.push(insights[0]);
    parts.push(`Top ${fallback.length} match${fallback.length === 1 ? '' : 'es'} below, ranked by fit.`);
    summary = parts.join('. ') + '.';
  }

  return {
    summary,
    suggestions: fallback.map((s) => ({ slug: s.entry.slug, score: s.score, reason: s.reason })),
  };
}

const EXAMPLE_PROMPTS = [
  'I run a restaurant in Madison and want to reach families',
  'Looking for cheap streaming inventory in the Midwest',
  'Premium primetime reach for a regional bank in Oklahoma',
  'Younger cord-cutter audience in Hawaii',
];

const BrowseChannelsPage: React.FC = () => {
  const { configs: allConfigs } = useAllPublisherConfigs();
  const navigate = useNavigate();
  const entries = useMemo(() => buildEntries(allConfigs), [allConfigs]);
  const entriesBySlug = useMemo(() => Object.fromEntries(entries.map((e) => [e.slug, e])), [entries]);
  const [prompt, setPrompt] = useState('');
  const [thinking, setThinking] = useState(false);
  const [result, setResult] = useState<AiResult | null>(null);

  const runAi = (text: string) => {
    if (!text.trim()) return;
    setPrompt(text);
    setThinking(true);
    setResult(null);
    window.setTimeout(() => {
      setResult(generateAiResponse(text, entries));
      setThinking(false);
    }, 900 + Math.random() * 600);
  };

  const totalChannels = entries.reduce((acc, e) => acc + e.channelCount, 0);
  const totalMarkets = new Set(entries.flatMap((e) => e.allDmas)).size;

  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [tableTab, setTableTab] = useState<'local' | 'national'>('local');

  const nationalEntries = useMemo(() => entries.filter((e) => e.coverage === 'national'), [entries]);
  const regionalEntries = useMemo(() => entries.filter((e) => e.coverage !== 'national'), [entries]);

  const visibleSuggestions = useMemo(() => result?.suggestions ?? [], [result?.suggestions]);

  const tabEntries = tableTab === 'national' ? nationalEntries : regionalEntries;

  // With AI picks, ignore tabs/state filter and show whatever the AI suggested
  // (a mix). Otherwise show the current tab's publishers, filtered by state
  // if one is selected. Memoized so map hovers and unrelated re-renders don't
  // rebuild a per-entry Set of states.
  const visibleEntries = useMemo<Array<{ entry: PublisherEntry; reason: string }>>(() => {
    if (visibleSuggestions.length > 0) {
      return visibleSuggestions.map((s) => ({ entry: entriesBySlug[s.slug], reason: s.reason }));
    }
    const base = tabEntries.map((e) => ({ entry: e, reason: '' }));
    if (!(selectedState && tableTab === 'local')) return base;
    return base.filter(({ entry }) => entry.allDmas.flatMap(dmaToStates).includes(selectedState));
  }, [visibleSuggestions, entriesBySlug, tabEntries, selectedState, tableTab]);

  // Map data: state → coverage count among "active" publishers (hovered, AI picks, or all)
  const activePublishers = useMemo(() => {
    if (hoveredSlug) {
      const e = entriesBySlug[hoveredSlug];
      return e ? [e] : [];
    }
    if (visibleSuggestions.length > 0) {
      return visibleSuggestions.map((s) => entriesBySlug[s.slug]).filter(Boolean);
    }
    return entries;
  }, [hoveredSlug, visibleSuggestions, entriesBySlug, entries]);

  const mapData = useMemo(() => {
    const counts: Record<string, number> = {};
    // Only regional publishers paint the map — nationals deliver everywhere and
    // would wash out the heatmap.
    for (const p of activePublishers.filter((p) => p.coverage !== 'national')) {
      const states = new Set(p.allDmas.flatMap(dmaToStates));
      for (const st of states) counts[st] = (counts[st] || 0) + 1;
    }
    return Object.entries(counts).map(([id, value]) => ({ id, value }));
  }, [activePublishers]);

  const maxCount = Math.max(1, ...mapData.map((d) => d.value));

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader eyebrow="Marketplace" title="Browse channels" />

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Browse channels and networks
          </h1>
          <p className="text-lg text-slate-700 max-w-2xl mx-auto">
            Explore every publisher and station in our marketplace — from local broadcast affiliates
            to national networks.
          </p>
          <div className="flex items-center justify-center gap-6 mt-5 text-sm text-slate-600">
            <span><strong className="text-slate-900">{entries.length}</strong> publishers</span>
            <span><strong className="text-slate-900">{totalChannels}</strong> channels</span>
            <span><strong className="text-slate-900">{totalMarkets}</strong> markets</span>
          </div>
        </div>

        {/* AI assistant */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-indigo-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">Ask the marketplace AI</div>
                <div className="text-xs text-slate-500">Describe your audience, geography, or goals</div>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                runAi(prompt);
              }}
            >
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    runAi(prompt);
                  }
                }}
                placeholder="e.g. I'm a regional auto dealer in Tennessee looking for primetime reach…"
                rows={2}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="flex flex-wrap gap-1.5">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => runAi(ex)}
                      className="text-xs px-2 py-1 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 hover:text-indigo-700"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={!prompt.trim() || thinking}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-gradient-to-br from-indigo-600 to-purple-600 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {thinking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {thinking ? 'Thinking…' : 'Suggest'}
                </button>
              </div>
            </form>

            {(thinking || result) && (
              <div className="mt-4 pt-4 border-t border-indigo-100">
                {thinking ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
                    Analyzing your goals across {entries.length} publishers…
                  </div>
                ) : result ? (
                  <div>
                    <p className="text-sm text-slate-700 leading-relaxed">{result.summary}</p>
                    {result.suggestions.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setResult(null); setPrompt(''); }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 mt-2"
                      >
                        Clear and show all publishers
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Map + Table side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Map */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-4 lg:sticky lg:top-4 lg:self-start">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-900">Coverage map</div>
              <div className="text-xs text-slate-500 flex items-center gap-2">
                {selectedState && (
                  <button
                    type="button"
                    onClick={() => setSelectedState(null)}
                    className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium hover:bg-indigo-200"
                  >
                    {selectedState} ✕
                  </button>
                )}
                <span>
                  {hoveredSlug
                    ? entriesBySlug[hoveredSlug]?.name
                    : visibleSuggestions.length > 0
                      ? `${activePublishers.length} AI picks`
                      : `${entries.length} publishers`}
                </span>
              </div>
            </div>
            <div style={{ width: '100%', position: 'relative' }}>
              <ComposableMap
                projection="geoAlbersUsa"
                projectionConfig={{ scale: 1000 }}
                width={800}
                height={500}
                style={{ width: '100%', height: 'auto' }}
              >
                <Geographies geography={US_TOPOJSON}>
                  {({ geographies }: { geographies: Array<{ rsmKey: string; id: string; properties: { name: string } }> }) =>
                    geographies.map((geo) => {
                      const usps = FIPS_TO_STATE[geo.id] ?? geo.id;
                      const count = mapData.find((d) => d.id === usps)?.value ?? 0;
                      // Covered states use indigo ramp; uncovered get neutral grey for clear contrast
                      const ramp = ['#c7d2fe', '#818cf8', '#6366f1', '#4338ca'];
                      let fill = '#ffffff';
                      if (count > 0) {
                        const idx = maxCount <= 1 ? ramp.length - 1 : Math.min(ramp.length - 1, Math.floor(((count - 1) / (maxCount - 1)) * (ramp.length - 1)));
                        fill = ramp[idx];
                      }
                      const isSelected = selectedState === usps;
                      if (isSelected) fill = '#312e81';
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={fill}
                          stroke={isSelected ? '#1e1b4b' : '#334155'}
                          strokeWidth={isSelected ? 1.5 : 1}
                          onClick={() => {
                            if (count > 0) {
                              setSelectedState((s) => (s === usps ? null : usps));
                              setTableTab('local');
                            }
                          }}
                          style={{
                            default: { outline: 'none' },
                            hover: { outline: 'none', fill: count > 0 ? '#312e81' : '#e2e8f0', cursor: count > 0 ? 'pointer' : 'default' },
                            pressed: { outline: 'none' },
                          }}
                        >
                          <title>{`${geo.properties.name}${count > 0 ? ` — ${count} ${count === 1 ? 'publisher' : 'publishers'}` : ''}`}</title>
                        </Geography>
                      );
                    })
                  }
                </Geographies>
              </ComposableMap>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>Click a state to filter · hover a row to focus</span>
              <div className="flex items-center gap-1">
                <span>fewer</span>
                <div className="flex">
                  {['#e0e7ff', '#a5b4fc', '#818cf8', '#6366f1', '#4338ca'].map((c) => (
                    <div key={c} className="w-3 h-2" style={{ backgroundColor: c }} />
                  ))}
                </div>
                <span>more</span>
              </div>
            </div>

          </div>

          {/* Table */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-200 bg-slate-100">
            <div className="inline-flex rounded-lg bg-white p-1 border border-slate-200 shadow-sm">
              {([
                { key: 'local', label: 'Local stations', count: regionalEntries.length },
                { key: 'national', label: 'National networks', count: nationalEntries.length },
              ] as const).map((t) => {
                const active = tableTab === t.key && visibleSuggestions.length === 0;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => { setTableTab(t.key); if (visibleSuggestions.length > 0) { setResult(null); setPrompt(''); } }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                      active
                        ? 'bg-indigo-600 text-white shadow'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {t.label}
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                      active ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                      {t.count}
                    </span>
                  </button>
                );
              })}
            </div>
            {visibleSuggestions.length > 0 && (
              <div className="ml-auto px-3 py-1.5 rounded-full bg-indigo-100 text-xs text-indigo-700 flex items-center gap-1.5 font-medium">
                <Sparkles className="w-3 h-3" />
                AI picks
              </div>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                <th className="px-3 py-3 w-12"></th>
                <th className="px-3 py-3">Publisher</th>
                <th className="px-3 py-3">Coverage</th>
                <th className="px-3 py-3 text-right">CPM</th>
                <th className="px-3 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {visibleEntries.map(({ entry: e, reason }, idx) => {
                const isAiPick = !!result && idx < 3 && !!reason;
                return (
                  <React.Fragment key={e.slug}>
                    <tr
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer group"
                      onClick={() => {
                        navigate(`/${e.slug}`);
                      }}
                      onMouseEnter={() => setHoveredSlug(e.slug)}
                      onMouseLeave={() => setHoveredSlug((s) => (s === e.slug ? null : s))}
                    >
                      <td className="px-3 py-3 align-middle">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden"
                          style={{ backgroundColor: e.primaryColor }}
                        >
                          {e.logoUrl ? (
                            <img src={e.logoUrl} alt={e.name} className="max-w-full max-h-full object-contain p-1" />
                          ) : (
                            <Tv className="w-4 h-4 text-white" />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Link
                            to={`/${e.slug}`}
                            onClick={(ev) => ev.stopPropagation()}
                            className="font-medium text-slate-900 hover:text-indigo-700 text-sm"
                          >
                            {e.name}
                          </Link>
                          {isAiPick && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white bg-gradient-to-br from-indigo-600 to-purple-600">
                              <Sparkles className="w-2.5 h-2.5" />#{idx + 1}
                            </span>
                          )}
                          {e.coverage === 'national' && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-slate-900 text-white">Natl</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                          {e.allChannels.join(' · ')}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="flex items-start gap-1 text-xs text-slate-600">
                          <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
                          <span className="line-clamp-2">{e.allDmas.join(' · ')}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-middle text-right font-semibold text-slate-900 text-sm whitespace-nowrap">
                        ${e.minCpm}
                      </td>
                      <td className="px-3 py-3 align-middle text-right">
                        <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                      </td>
                    </tr>
                    {isAiPick && (
                      <tr className="bg-indigo-50/40 border-b border-slate-100">
                        <td></td>
                        <td colSpan={4} className="px-3 py-2 text-xs text-indigo-900">
                          <div className="flex items-start gap-1.5">
                            <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0 text-indigo-600" />
                            <span>{reason}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BrowseChannelsPage;
